import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DropletIcon, ScaleIcon } from 'lucide-react';
interface DosingInteractiveProps {
  accent: string;
}
const AMH_THRESHOLD = 15;
export function DosingInteractive({
  accent
}: DosingInteractiveProps) {
  const [amh, setAmh] = useState(24);
  const [weight, setWeight] = useState(65);
  const lowAmh = amh < AMH_THRESHOLD;
  const relative = lowAmh ? 1 : Math.min(1, Math.max(0.1, (weight - 45) / 55 * 0.5 + (1 - (amh - AMH_THRESHOLD) / 35) * 0.5));
  const sliders = [{
    id: 'amh',
    icon: DropletIcon,
    label: 'Serum AMH',
    unit: 'pmol/L',
    value: amh,
    min: 5,
    max: 50,
    set: setAmh
  }, {
    id: 'weight',
    icon: ScaleIcon,
    label: 'Body weight',
    unit: 'kg',
    value: weight,
    min: 45,
    max: 100,
    set: setWeight
  }];
  return <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-5" data-id="element-146" data-mp="2uituvl9du5zs">
      <div className="space-y-4" data-id="element-147" data-mp="2zh56o1j8mqkn">
        {sliders.map(({
        id,
        icon: Icon,
        label,
        unit,
        value,
        min,
        max,
        set
      }) => <div key={id} className="rounded-2xl border border-navy-900/[0.08] bg-white p-4" data-id="element-148" data-mp="1tlrj0clto0t3">
            <div className="flex items-center justify-between" data-id="element-149" data-mp="1ousw2u6bw4gv">
              <label htmlFor={`slider-${id}`} className="flex items-center gap-2 text-[13px] font-medium text-navy-900" data-id="element-150" data-mp="1osg922lpr3wy">
                <Icon className="h-4 w-4" style={{
              color: accent
            }} data-id="element-151" data-mp="1ewd9n4i8xjdp" />
                {label}
              </label>
              <span className="text-[15px] font-semibold tabular-nums text-navy-900" data-id="element-152" data-mp="2cknb9ie4e54a">
                {value} <span className="text-[11px] font-normal text-slate-400" data-id="element-153" data-mp="27lafg859tnpk">{unit}</span>
              </span>
            </div>
            <input id={`slider-${id}`} type="range" min={min} max={max} value={value} onChange={e => set(Number(e.target.value))} className="mt-3 w-full" style={{
          accentColor: accent
        }} data-id="element-154" data-mp="22kwflddtpk2m" />
          </div>)}
      </div>

      <svg width="48" height="120" viewBox="0 0 48 120" aria-hidden="true" data-id="element-155" data-mp="n7jrv2c73y20">
        <motion.path d="M2 30 C 24 30, 24 60, 46 60 M2 90 C 24 90, 24 60, 46 60" fill="none" stroke={accent} strokeWidth="2" strokeDasharray="4 4" animate={{
        strokeDashoffset: [0, -16]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-156" data-mp="on4b6odwg5l8" />
      </svg>

      <div className="rounded-3xl p-5 text-white" style={{
      backgroundColor: '#0F2442'
    }} data-id="element-157" data-mp="5w4sg4wzzs0e">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-teal-300" data-id="element-158" data-mp="aml9dz011vri">Individualised daily dose</p>
        <p className="mt-2 text-[17px] font-semibold leading-snug" data-id="element-159" data-mp="3s3umnis93aub">Determined from the SmPC dosing table</p>
        <div className="mt-5" data-id="element-160" data-mp="vyuk704x99v">
          <div className="flex justify-between text-[10px] uppercase tracking-wider text-white/50" data-id="element-161" data-mp="x1nslkxjnsy">
            <span data-id="element-162" data-mp="3j36irgwkmqv5">Lower</span>
            <span data-id="element-163" data-mp="3n3yseb9cuxry">Higher</span>
          </div>
          <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-white/10" data-id="element-164" data-mp="3n2ot5iqrnjjq">
            <motion.div className="h-full rounded-full" style={{
            backgroundColor: accent
          }} animate={{
            width: `${relative * 100}%`
          }} transition={{
            type: 'spring',
            stiffness: 120,
            damping: 20
          }} data-id="element-165" data-mp="34ewhp5hl7uhe" />
          </div>
          <p className="mt-3 text-[12px] leading-relaxed text-white/70" data-id="element-166" data-mp="38hjap9pzvlg6">
            {lowAmh ? 'Below the AMH threshold, a fixed daily dose applies regardless of body weight (see SmPC).' : 'Higher AMH → relatively lower dose. Higher body weight → relatively higher dose.'}
          </p>
        </div>
        <p className="mt-4 border-t border-white/10 pt-3 text-[10px] leading-relaxed text-white/45" data-id="element-167" data-mp="esd0ls3can">
          Illustrative relationship only. Not for dose calculation — always refer to the SmPC.
        </p>
      </div>
    </div>;
}